import React, { useState, useEffect, useMemo, useRef } from 'react';
import { Calendar, Filter, X, Download, ChevronDown, ChevronUp, Search, RotateCcw, Truck as TruckIcon, User, MapPin, Weight, Clock, Hash } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { STATUS_OPTIONS } from '../constants/statuses';

const MONTH_NAMES = [
  'Janvier', 'Fevrier', 'Mars', 'Avril', 'Mai', 'Juin',
  'Juillet', 'Aout', 'Septembre', 'Octobre', 'Novembre', 'Decembre'
];

const MultiSelectDropdown = ({ options, selected, onChange, placeholder, icon: Icon, searchable = true }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef(null);

  useEffect(() => {
    const handleClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const filtered = search
    ? options.filter(o => o.label.toLowerCase().includes(search.toLowerCase()))
    : options;

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        className="flex items-center justify-between w-full rounded-md border border-gray-200 bg-white px-3 py-2 text-sm hover:bg-gray-50 text-left"
        onClick={() => setOpen(!open)}
      >
        <span className="flex items-center gap-2 text-gray-600 truncate">
          {Icon && <Icon className="w-4 h-4 shrink-0" />}
          {selected.length === 0 ? placeholder : `${selected.length} selectionne(s)`}
        </span>
        <ChevronDown className="w-4 h-4 shrink-0 text-gray-400" />
      </button>
      {selected.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-1">
          {selected.map(val => {
            const opt = options.find(o => o.value === val);
            return (
              <Badge key={val} variant="secondary" className="text-xs px-1.5 py-0 gap-1">
                {opt?.label || val}
                <X className="w-3 h-3 cursor-pointer" onClick={() => onChange(selected.filter(s => s !== val))} />
              </Badge>
            );
          })}
        </div>
      )}
      {open && (
        <div className="absolute z-50 mt-1 w-full bg-white border rounded-md shadow-lg max-h-60 overflow-auto">
          {searchable && (
            <div className="p-2 border-b sticky top-0 bg-white">
              <Input
                placeholder="Rechercher..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="h-8 text-sm"
                autoFocus
              />
            </div>
          )}
          {filtered.map(opt => (
            <div
              key={opt.value}
              className={`px-3 py-2 text-sm cursor-pointer hover:bg-gray-50 flex items-center gap-2 ${selected.includes(opt.value) ? 'bg-blue-50 text-blue-700' : ''}`}
              onClick={() => {
                if (selected.includes(opt.value)) {
                  onChange(selected.filter(s => s !== opt.value));
                } else {
                  onChange([...selected, opt.value]);
                }
              }}
            >
              <input type="checkbox" checked={selected.includes(opt.value)} readOnly className="rounded" />
              {opt.label}
            </div>
          ))}
          {filtered.length === 0 && <div className="p-3 text-sm text-gray-400 text-center">Aucun resultat</div>}
        </div>
      )}
    </div>
  );
};

const AdvancedSearchPanel = ({ operations, onFilter, onReset, statusOptions: customStatusOptions }) => {
  // Field accessor helpers - handle both operations and transfer/runs data shapes
  const getDate = (op) => op.deliveryDate || op.date || op.createdAt;
  const getDeparture = (op) => op.departure || op.departureAddress || op.stops?.[0]?.address?.split(',')[0] || '';
  const getArrival = (op) => op.arrival || op.arrivalAddress || (op.stops?.length > 0 ? op.stops[op.stops.length - 1]?.address?.split(',')[0] : '') || '';
  const getVehicle = (op) => op.vehiclePlate || op.assignedVehiclePlate || op.plannedVehiclePlate || '';
  const getDriverName = (op) => op.driverName || op.assignedDriverName || op.plannedDriverName || '';
  const getDriverId = (op) => op.driverId || op.assignedDriverId || getDriverName(op);
  const getEstimatedWeight = (op) => parseFloat(op.weight || op.estimatedWeight || 0);
  const getNetWeight = (op) => {
    if (op.netWeight != null && op.netWeight > 0) return parseFloat(op.netWeight);
    if (op.actualWeight) return parseFloat(op.actualWeight);
    if (op.tripsNetWeights?.length > 0) return op.tripsNetWeights.reduce((s, w) => s + (w || 0), 0);
    return 0;
  };

  const effectiveStatusOptions = customStatusOptions || STATUS_OPTIONS;

  const [isOpen, setIsOpen] = useState(false);
  const [filters, setFilters] = useState({
    dateFrom: '',
    dateTo: '',
    month: '',
    departures: [],
    arrivals: [],
    weightMin: '',
    weightMax: '',
    weightType: 'estimated',
    vehicles: [],
    drivers: [],
    statuses: [],
    externalRefs: [],
  });

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (filters.dateFrom || filters.dateTo || filters.month) count++;
    if (filters.departures.length > 0) count++;
    if (filters.arrivals.length > 0) count++;
    if (filters.weightMin || filters.weightMax) count++;
    if (filters.vehicles.length > 0) count++;
    if (filters.drivers.length > 0) count++;
    if (filters.statuses.length > 0) count++;
    if (filters.externalRefs.length > 0) count++;
    return count;
  }, [filters]);

  // Extract unique values from operations for dropdowns
  const departureOptions = useMemo(() => {
    const cities = [...new Set(operations.map(op => getDeparture(op)).filter(Boolean))].sort();
    return cities.map(c => ({ value: c, label: c }));
  }, [operations]);

  const arrivalOptions = useMemo(() => {
    const cities = [...new Set(operations.map(op => getArrival(op)).filter(Boolean))].sort();
    return cities.map(c => ({ value: c, label: c }));
  }, [operations]);

  const vehicleOptions = useMemo(() => {
    const vehicles = new Map();
    operations.forEach(op => {
      const v = getVehicle(op);
      if (v) vehicles.set(v, v);
    });
    return [...vehicles.entries()].map(([k, v]) => ({ value: k, label: v })).sort((a, b) => a.label.localeCompare(b.label));
  }, [operations]);

  const driverOptions = useMemo(() => {
    const drivers = new Map();
    operations.forEach(op => {
      const name = getDriverName(op);
      const id = getDriverId(op);
      if (name && id) drivers.set(id, name);
    });
    return [...drivers.entries()].map(([k, v]) => ({ value: k, label: v })).sort((a, b) => a.label.localeCompare(b.label));
  }, [operations]);

  // Références externes client (n° dossier, n° commande…) présentes dans les données
  const externalRefOptions = useMemo(() => {
    const refs = [...new Set(operations.map(op => (op.externalRef || '').trim()).filter(Boolean))].sort();
    return refs.map(r => ({ value: r, label: r }));
  }, [operations]);

  const externalRefLabel = useMemo(() => {
    const op = operations.find(o => o.externalRef && o.externalRefLabel);
    return op?.externalRefLabel || 'Référence externe';
  }, [operations]);

  // Generate month options (last 24 months)
  const monthOptions = useMemo(() => {
    const months = [];
    const now = new Date();
    for (let i = 0; i < 24; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const key = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      months.push({ value: key, label: `${MONTH_NAMES[d.getMonth()]} ${d.getFullYear()}` });
    }
    return months;
  }, []);

  // Re-apply filters when operations data changes (e.g. refetch)
  const prevOpsLenRef = useRef(operations.length);
  const filtersRef = useRef(filters);
  filtersRef.current = filters;
  useEffect(() => {
    if (operations.length !== prevOpsLenRef.current) {
      prevOpsLenRef.current = operations.length;
      const f = filtersRef.current;
      const hasActiveFilter = f.dateFrom || f.dateTo || f.month || f.departures.length || f.arrivals.length || f.weightMin || f.weightMax || f.vehicles.length || f.drivers.length || f.statuses.length || f.externalRefs.length;
      if (hasActiveFilter) {
        applyFiltersInternal(f, operations, onFilter);
      }
    }
  }, [operations, onFilter]);

  const applyFiltersInternal = (f, data, callback) => {
    let result = [...data];

    // Date filter
    if (f.month) {
      const [year, month] = f.month.split('-').map(Number);
      const start = new Date(year, month - 1, 1);
      const end = new Date(year, month, 0, 23, 59, 59);
      result = result.filter(op => { const d = new Date(getDate(op)); return d >= start && d <= end; });
    } else {
      if (f.dateFrom) { const start = new Date(f.dateFrom); result = result.filter(op => new Date(getDate(op)) >= start); }
      if (f.dateTo) { const end = new Date(f.dateTo + 'T23:59:59'); result = result.filter(op => new Date(getDate(op)) <= end); }
    }

    // Departure
    if (f.departures.length > 0) {
      result = result.filter(op => f.departures.includes(getDeparture(op)));
    }

    // Arrival
    if (f.arrivals.length > 0) {
      result = result.filter(op => f.arrivals.includes(getArrival(op)));
    }

    // Weight
    if (f.weightMin || f.weightMax) {
      const getW = (op) => f.weightType === 'net' ? getNetWeight(op) : getEstimatedWeight(op);
      if (f.weightMin) { const min = parseFloat(f.weightMin); result = result.filter(op => getW(op) >= min); }
      if (f.weightMax) { const max = parseFloat(f.weightMax); result = result.filter(op => getW(op) <= max); }
    }

    // Vehicles
    if (f.vehicles.length > 0) {
      result = result.filter(op => f.vehicles.includes(getVehicle(op)));
    }

    // Drivers
    if (f.drivers.length > 0) {
      result = result.filter(op => f.drivers.includes(getDriverId(op)) || f.drivers.includes(getDriverName(op)));
    }

    // Statuses
    if (f.statuses.length > 0) {
      result = result.filter(op => f.statuses.includes(op.status));
    }

    // Références externes client (n° dossier…)
    if (f.externalRefs.length > 0) {
      result = result.filter(op => f.externalRefs.includes((op.externalRef || '').trim()));
    }

    callback(result);
  };

  const applyFilters = (newFilters) => {
    applyFiltersInternal(newFilters || filters, operations, onFilter);
  };

  const updateFilter = (key, value) => {
    const updated = { ...filters, [key]: value };
    // If month is set, clear date range and vice versa
    if (key === 'month' && value) {
      updated.dateFrom = '';
      updated.dateTo = '';
    }
    if ((key === 'dateFrom' || key === 'dateTo') && value) {
      updated.month = '';
    }
    setFilters(updated);
    applyFilters(updated);
  };

  const setPresetPeriod = (preset) => {
    const now = new Date();
    let from, to;
    switch (preset) {
      case 'this_month':
        from = new Date(now.getFullYear(), now.getMonth(), 1);
        to = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        break;
      case 'last_month':
        from = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        to = new Date(now.getFullYear(), now.getMonth(), 0);
        break;
      case 'last_3_months':
        from = new Date(now.getFullYear(), now.getMonth() - 2, 1);
        to = new Date(now.getFullYear(), now.getMonth() + 1, 0);
        break;
      case 'this_year':
        from = new Date(now.getFullYear(), 0, 1);
        to = new Date(now.getFullYear(), 11, 31);
        break;
      default:
        return;
    }
    const fmt = (d) => d.toISOString().split('T')[0];
    const updated = { ...filters, dateFrom: fmt(from), dateTo: fmt(to), month: '' };
    setFilters(updated);
    applyFilters(updated);
  };

  const resetFilters = () => {
    const empty = {
      dateFrom: '', dateTo: '', month: '',
      departures: [], arrivals: [],
      weightMin: '', weightMax: '', weightType: 'estimated',
      vehicles: [], drivers: [], statuses: [], externalRefs: [],
    };
    setFilters(empty);
    if (onReset) onReset();
    else onFilter(operations);
  };

  const handleExportCSV = () => {
    // Apply filters to get current filtered set
    let filtered = [...operations];
    applyFiltersInternal(filters, operations, (res) => { filtered = res; });

    // Generate CSV using helper functions
    const headers = ['Reference', externalRefLabel, 'Depart', 'Arrivee', 'Date', 'Poids estime', 'Poids net', 'Statut', 'Vehicule', 'Chauffeur'];
    const rows = filtered.map(op => [
      op.operationNumber || op.runNumber || op.id || '',
      op.externalRef || '',
      getDeparture(op),
      getArrival(op),
      getDate(op) ? new Date(getDate(op)).toLocaleDateString('fr-FR') : '',
      getEstimatedWeight(op) || '',
      getNetWeight(op) || '',
      op.status || '',
      getVehicle(op),
      getDriverName(op)
    ]);

    const csvContent = [headers, ...rows].map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
    const BOM = '\uFEFF';
    const blob = new Blob([BOM + csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    const date = new Date().toISOString().split('T')[0];
    a.href = url;
    a.download = `export_${date}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-2" data-testid="advanced-search-panel">
      {/* Toggle bar */}
      <div className="flex items-center gap-2">
        <Button
          variant={isOpen ? 'default' : 'outline'}
          size="sm"
          onClick={() => setIsOpen(!isOpen)}
          className="gap-2"
          data-testid="toggle-advanced-search"
        >
          <Filter className="w-4 h-4" />
          Filtres avances
          {activeFilterCount > 0 && (
            <Badge variant="destructive" className="ml-1 h-5 min-w-[20px] px-1 text-xs">{activeFilterCount}</Badge>
          )}
          {isOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </Button>

        <Button
          variant="outline"
          size="sm"
          onClick={handleExportCSV}
          className="gap-2"
          data-testid="export-csv-btn"
        >
          <Download className="w-4 h-4" />
          Exporter CSV
        </Button>

        {activeFilterCount > 0 && (
          <Button variant="ghost" size="sm" onClick={resetFilters} className="gap-1 text-gray-500" data-testid="reset-filters-btn">
            <RotateCcw className="w-3 h-3" />
            Reinitialiser
          </Button>
        )}
      </div>

      {/* Collapsible panel */}
      {isOpen && (
        <div className="bg-gray-50 border rounded-lg p-4 space-y-4" data-testid="advanced-filters-panel">
          {/* Period */}
          <div>
            <Label className="text-xs font-semibold text-gray-500 uppercase tracking-wider flex items-center gap-1 mb-2">
              <Calendar className="w-3 h-3" /> Période
            </Label>
            <div className="flex flex-wrap gap-2 mb-2">
              {[
                { key: 'this_month', label: 'Ce mois' },
                { key: 'last_month', label: 'Mois dernier' },
                { key: 'last_3_months', label: '3 derniers mois' },
                { key: 'this_year', label: 'Cette annee' }
              ].map(p => (
                <Button key={p.key} variant="outline" size="sm" className="text-xs h-7" onClick={() => setPresetPeriod(p.key)} data-testid={`preset-${p.key}`}>
                  {p.label}
                </Button>
              ))}
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <Select value={filters.month} onValueChange={(v) => updateFilter('month', v === 'none' ? '' : v)}>
                <SelectTrigger className="w-48 h-9 text-sm" data-testid="month-select">
                  <SelectValue placeholder="Selectionner un mois" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Tous les mois</SelectItem>
                  {monthOptions.map(m => (
                    <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <span className="text-xs text-gray-400">ou</span>
              <div className="flex items-center gap-1">
                <span className="text-xs text-gray-500">Du</span>
                <Input
                  type="date"
                  value={filters.dateFrom}
                  onChange={(e) => updateFilter('dateFrom', e.target.value)}
                  className="h-9 w-40 text-sm"
                  data-testid="date-from"
                />
                <span className="text-xs text-gray-500">Au</span>
                <Input
                  type="date"
                  value={filters.dateTo}
                  onChange={(e) => updateFilter('dateTo', e.target.value)}
                  className="h-9 w-40 text-sm"
                  data-testid="date-to"
                />
              </div>
            </div>
          </div>

          {/* Route */}
          <div>
            <Label className="text-xs font-semibold text-gray-500 uppercase tracking-wider flex items-center gap-1 mb-2">
              <MapPin className="w-3 h-3" /> Trajet
            </Label>
            <div className="flex flex-wrap gap-3">
              <div className="w-64">
                <MultiSelectDropdown
                  options={departureOptions}
                  selected={filters.departures}
                  onChange={(v) => updateFilter('departures', v)}
                  placeholder="Villes de depart"
                  icon={MapPin}
                />
              </div>
              <div className="w-64">
                <MultiSelectDropdown
                  options={arrivalOptions}
                  selected={filters.arrivals}
                  onChange={(v) => updateFilter('arrivals', v)}
                  placeholder="Villes d'arrivee"
                  icon={MapPin}
                />
              </div>
            </div>
          </div>

          {/* Weight + Vehicles/Drivers row */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Weight */}
            <div>
              <Label className="text-xs font-semibold text-gray-500 uppercase tracking-wider flex items-center gap-1 mb-2">
                <Weight className="w-3 h-3" /> Poids (tonnes)
              </Label>
              <div className="flex items-center gap-2">
                <Select value={filters.weightType} onValueChange={(v) => updateFilter('weightType', v)}>
                  <SelectTrigger className="w-32 h-9 text-sm" data-testid="weight-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="estimated">Estime</SelectItem>
                    <SelectItem value="net">Net</SelectItem>
                  </SelectContent>
                </Select>
                <Input
                  type="number"
                  placeholder="Min"
                  value={filters.weightMin}
                  onChange={(e) => updateFilter('weightMin', e.target.value)}
                  className="h-9 w-24 text-sm"
                  data-testid="weight-min"
                />
                <span className="text-xs text-gray-400">a</span>
                <Input
                  type="number"
                  placeholder="Max"
                  value={filters.weightMax}
                  onChange={(e) => updateFilter('weightMax', e.target.value)}
                  className="h-9 w-24 text-sm"
                  data-testid="weight-max"
                />
              </div>
            </div>

            {/* Status */}
            <div>
              <Label className="text-xs font-semibold text-gray-500 uppercase tracking-wider flex items-center gap-1 mb-2">
                <Clock className="w-3 h-3" /> Statut
              </Label>
              <MultiSelectDropdown
                options={effectiveStatusOptions}
                selected={filters.statuses}
                onChange={(v) => updateFilter('statuses', v)}
                placeholder="Tous les statuts"
                icon={Clock}
              />
            </div>
          </div>

          {/* Référence externe client (n° dossier, n° commande…) */}
          {externalRefOptions.length > 0 && (
            <div data-testid="advanced-external-ref-filter">
              <Label className="text-xs font-semibold text-gray-500 uppercase tracking-wider flex items-center gap-1 mb-2">
                <Hash className="w-3 h-3" /> {externalRefLabel}
              </Label>
              <div className="md:w-1/2">
                <MultiSelectDropdown
                  options={externalRefOptions}
                  selected={filters.externalRefs}
                  onChange={(v) => updateFilter('externalRefs', v)}
                  placeholder={`Toutes les références (${externalRefOptions.length})`}
                  icon={Hash}
                />
              </div>
            </div>
          )}

          {/* Vehicles / Drivers */}
          <div>
            <Label className="text-xs font-semibold text-gray-500 uppercase tracking-wider flex items-center gap-1 mb-2">
              <TruckIcon className="w-3 h-3" /> Véhicule / Chauffeur
            </Label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <MultiSelectDropdown
                options={vehicleOptions}
                selected={filters.vehicles}
                onChange={(v) => updateFilter('vehicles', v)}
                placeholder="Selectionner des vehicules"
                icon={TruckIcon}
              />
              <MultiSelectDropdown
                options={driverOptions}
                selected={filters.drivers}
                onChange={(v) => updateFilter('drivers', v)}
                placeholder="Selectionner des chauffeurs"
                icon={User}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdvancedSearchPanel;
